import { useRef } from 'react';
import { useFormik } from 'formik';
import * as yup from 'yup';
import * as K from 'common/constants';
import Input from 'components/common/Input/Input';
import Button, { ButtonVariant } from 'components/common/Button/Button';
import { gqlRequest } from 'common/util/gqlRequest';
import { AUTH_PAGE_LOGIN_TYPE } from './constants';
import AuthPageFooter, { getRememberMeValue } from './AuthPageFooter';
import { USER_LOGIN_STATE } from './queries';

type SSOLoginProps = {
  changeAuthMethod: () => void;
  getRedirectUrl: () => string;
  setStage: (arg0: AUTH_PAGE_LOGIN_TYPE) => void;
  setError: (error: any) => void;
  setStatus: (status: any) => void;
  setEmailForPassword: (email: string) => void;
  status: any;
  emailToDisplay: string | undefined;
};

const loginValidationSchema = yup.object({
  email: yup.string().email().required('Please enter a valid email address'),
});

function SSOLogin({
  changeAuthMethod,
  getRedirectUrl,
  setStage,
  setError,
  setStatus,
  status,
  setEmailForPassword,
  emailToDisplay,
}: SSOLoginProps): JSX.Element {
  const formEl = useRef<HTMLFormElement>(null);
  const formik = useFormik({
    initialValues: {
      email: emailToDisplay || '',
    },
    onSubmit: async () => {
      /*
       * Before we submit the SSO Login form, we make a GQL query to confirm whether user really has an SSO account or not.
       * If not, we will switch the view to 'Login with Password'
       * If user has SSO account, we submit the form
       */
      const email = formEl && formEl.current ? (formEl.current.elements[0] as HTMLInputElement) : { value: '' };
      try {
        setStatus(K.COMPONENT_STATUS.PENDING);
        const {
          userLoginState: { ssoLoginEnabled },
        } = await gqlRequest({ query: USER_LOGIN_STATE, variables: { email: email.value } });
        if (!ssoLoginEnabled) {
          setStage(AUTH_PAGE_LOGIN_TYPE.EMAIL_PASSWORD);
          setStatus(K.COMPONENT_STATUS.RESOLVED);
          setEmailForPassword(email.value);
        } else {
          if (getRememberMeValue()) {
            localStorage.setItem('userEmail', email.value);
          } else {
            localStorage.setItem('userEmail', '');
          }
          localStorage.setItem('loginType', AUTH_PAGE_LOGIN_TYPE.SSO);

          // eslint-disable-next-line @typescript-eslint/no-unused-expressions
          formEl && formEl.current && formEl.current.submit();
        }
      } catch (err) {
        setStatus(K.COMPONENT_STATUS.REJECTED);
        setError(err);
      }
    },
    validationSchema: loginValidationSchema,
  });

  return (
    <>
      <form ref={formEl} className="AuthPage__Form" method="POST" onSubmit={formik.handleSubmit} action="/v2/sso/login">
        <div className="AuthPage__Form__Body">
          <Input
            type="email"
            name="email"
            placeholder="Email address"
            value={formik.values.email}
            inputClassName="form-control-lg"
            onChange={formik.handleChange}
            isInvalid={formik.touched.email && !!formik.errors.email}
            errorMessage={formik.errors.email}
          />
          <input type="hidden" name="originalUrl" value={getRedirectUrl && getRedirectUrl()} />
          <Button
            variant={ButtonVariant.PRIMARY}
            type="submit"
            size="lg"
            disabled={status === K.COMPONENT_STATUS.PENDING}
            style={{ justifyContent: 'center' }}
          >
            Continue
          </Button>
          <a href="#" onClick={changeAuthMethod} style={{ textAlign: 'center', marginTop: 16 }}>
            Log in using Password
          </a>
        </div>
        <AuthPageFooter />
      </form>
    </>
  );
}

export default SSOLogin;
